import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { rupiah, formatNumber } from "../lib/format";
import { RupiahInput } from "../components/RupiahInput";
import { PrinterDialog } from "../components/PrinterDialog";
import { printThermal, printerConnected, reconnectPrinter } from "../lib/thermal";
import { getQueue, setQueue, enqueueSale, cacheItems, getCachedItems, buildOfflineSale } from "../lib/offline";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Search, Plus, Minus, Trash2, PauseCircle, PlayCircle, LogOut, Store,
  Wifi, WifiOff, ShoppingCart, Printer, Percent, CloudUpload, RefreshCw,
  LayoutDashboard, Package, ReceiptText, FileBarChart, Wallet, Boxes, Truck, Pencil,
} from "lucide-react";
import { toast } from "sonner";

const PAY = [
  { key: "cash", label: "Tunai" },
  { key: "transfer", label: "Transfer" },
  { key: "qris", label: "QRIS" },
  { key: "credit", label: "Hutang" },
];

const QUICK_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/items", label: "Barang", icon: Package },
  { to: "/stock", label: "Stok", icon: Boxes },
  { to: "/purchases", label: "Pembelian", icon: Truck },
  { to: "/sales", label: "Penjualan", icon: ReceiptText },
  { to: "/finance", label: "Kas", icon: Wallet },
  { to: "/reports", label: "Laporan", icon: FileBarChart },
];

export default function POS() {
  const { user, logout } = useAuth();
  const { online, setOnline } = useApp();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [held, setHeld] = useState(null);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [payment, setPayment] = useState("cash");
  const [paid, setPaid] = useState(0);
  const [customer, setCustomer] = useState("");
  const [printReceipt, setPrintReceipt] = useState(true);
  const [lastSale, setLastSale] = useState(null);
  const [discOpen, setDiscOpen] = useState(null);
  const [priceEdit, setPriceEdit] = useState(null);
  const [approverUser, setApproverUser] = useState("");
  const [approverPass, setApproverPass] = useState("");
  const [savingPrice, setSavingPrice] = useState(false);
  const [printerOpen, setPrinterOpen] = useState(false);
  const [queue, setQueueState] = useState(getQueue());
  const [syncing, setSyncing] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const searchRef = useRef(null);
  const isStaff = ["master", "admin"].includes(user.role);

  const loadItems = useCallback(() => api.get("/items")
    .then((r) => { const act = r.data.filter((i) => i.active); setItems(act); cacheItems(act); })
    .catch(() => { const c = getCachedItems(); if (c.length) { setItems(c); toast.info("Mode offline: memakai data barang tersimpan"); } }), []);
  useEffect(() => { loadItems(); searchRef.current?.focus(); }, [loadItems]);
  useEffect(() => { reconnectPrinter().catch(() => {}); }, []);

  const refreshQueue = () => setQueueState(getQueue());

  const syncQueue = useCallback(async (silent = false) => {
    const q = getQueue();
    if (!q.length || syncing) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/sales/sync", {
        sales: q.map((s) => ({
          client_id: s.client_id, offline_at: s.offline_at,
          items: s.items.map((l) => ({ item_id: l.item_id, name: l.name, qty: l.qty, price: l.price, discount: l.discount })),
          total_discount: s.total_discount, payment_method: s.payment_method,
          paid_amount: s.paid_amount, customer: s.customer, print_receipt: false,
        })),
      });
      const okIds = new Set(data.results.filter((r) => r.ok).map((r) => r.client_id));
      const errs = Object.fromEntries(data.results.filter((r) => !r.ok).map((r) => [r.client_id, r.error]));
      const remain = q.filter((s) => !okIds.has(s.client_id)).map((s) => ({ ...s, error: errs[s.client_id] }));
      setQueue(remain); refreshQueue();
      if (okIds.size) toast.success(`${okIds.size} transaksi offline tersinkron`);
      if (remain.length && !silent) toast.error(`${remain.length} transaksi gagal sinkron`);
      loadItems();
    } catch {
      if (!silent) toast.error("Sinkronisasi gagal, akan dicoba lagi");
    } finally { setSyncing(false); }
  }, [syncing, loadItems]);

  useEffect(() => {
    if (online && navigator.onLine && queue.length) syncQueue(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);
  useEffect(() => {
    const t = setInterval(() => { if (online && navigator.onLine && getQueue().some((s) => !s.error)) syncQueue(true); }, 30000);
    return () => clearInterval(t);
  }, [online, syncQueue]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items.slice(0, 60);
    return items.filter((i) =>
      i.name.toLowerCase().includes(q) ||
      i.barcode.toLowerCase().includes(q) ||
      (i.category || "").toLowerCase().includes(q)
    ).slice(0, 60);
  }, [items, search]);

  const addToCart = (item) => {
    if (item.stock <= 0) { toast.error(`Stok ${item.name} habis`); return; }
    setCart((c) => {
      const ex = c.find((l) => l.item_id === item.id);
      if (ex) {
        if (ex.qty + 1 > item.stock) { toast.error("Stok tidak cukup"); return c; }
        return c.map((l) => l.item_id === item.id ? { ...l, qty: l.qty + 1 } : l);
      }
      return [...c, { item_id: item.id, name: item.name, price: item.price_sell, qty: 1, discount: 0, stock: item.stock }];
    });
  };

  const setQty = (id, qty) => {
    setCart((c) => c.map((l) => {
      if (l.item_id !== id) return l;
      const q = Math.max(1, Math.min(qty, l.stock));
      return { ...l, qty: q };
    }));
  };
  const removeLine = (id) => setCart((c) => c.filter((l) => l.item_id !== id));

  const openPriceEdit = (l) => {
    if (user.role === "kasir" && (!online || !navigator.onLine)) {
      toast.error("Ubah harga butuh koneksi online untuk otorisasi");
      return;
    }
    setApproverUser(""); setApproverPass("");
    setPriceEdit({ item_id: l.item_id, name: l.name, price: l.price, original: l.price });
  };
  const savePrice = async () => {
    if (!priceEdit) return;
    const newPrice = Number(priceEdit.price) || 0;
    if (newPrice <= 0) { toast.error("Harga harus lebih dari 0"); return; }
    setSavingPrice(true);
    try {
      if (user.role === "kasir") {
        if (!approverUser.trim() || !approverPass) { toast.error("Masukkan username & password Master/Admin/GM"); setSavingPrice(false); return; }
        await api.post("/auth/verify-approver", {
          username: approverUser.trim(), password: approverPass,
          item_name: priceEdit.name, old_price: priceEdit.original, new_price: newPrice,
        });
      }
      setCart((c) => c.map((l) => l.item_id === priceEdit.item_id ? { ...l, price: newPrice } : l));
      toast.success("Harga diperbarui");
      setPriceEdit(null); setApproverUser(""); setApproverPass("");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSavingPrice(false); }
  };

  const barcodeEnter = (e) => {
    if (e.key === "Enter") {
      const exact = items.find((i) => i.barcode.toLowerCase() === search.trim().toLowerCase());
      if (exact) { addToCart(exact); setSearch(""); }
      else if (filtered.length === 1) { addToCart(filtered[0]); setSearch(""); }
    }
  };

  const subtotal = cart.reduce((s, l) => s + l.qty * l.price - l.discount, 0);
  const grandTotal = Math.max(0, subtotal - totalDiscount);
  const change = Math.max(0, (paid || 0) - grandTotal);
  const kurang = Math.max(0, grandTotal - (paid || 0));
  const isCredit = payment === "credit";
  const canPay = cart.length > 0 && (isCredit ? customer.trim().length > 0 : (paid || 0) >= grandTotal);

  const hold = () => {
    if (!cart.length) return;
    if (held) { toast.error("Sudah ada 1 transaksi ditahan (maks 1)"); return; }
    setHeld({ cart, totalDiscount, customer });
    setCart([]); setTotalDiscount(0); setCustomer("");
    toast.success("Transaksi ditahan");
  };
  const resume = () => {
    if (!held) return;
    if (cart.length) { toast.error("Selesaikan keranjang aktif dulu"); return; }
    setCart(held.cart); setTotalDiscount(held.totalDiscount); setCustomer(held.customer || "");
    setHeld(null);
    toast.success("Transaksi dilanjutkan");
  };

  const doPrint = async (sale) => {
    if (!printReceipt) return;
    let connected = printerConnected();
    if (!connected) {
      try { connected = !!(await reconnectPrinter()); } catch { connected = false; }
    }
    if (connected) {
      try { await printThermal(sale, user.name); toast.success("Struk dicetak ke printer thermal"); return; }
      catch { toast.error("Gagal kirim ke printer thermal, menampilkan preview"); }
    }
    // Tidak terhubung ke printer thermal → tampilkan preview print browser dulu
    setTimeout(() => window.print(), 400);
  };

  const finishLocal = (sale) => {
    setLastSale(sale);
    setCart([]); setTotalDiscount(0); setCustomer(""); setPaid(0);
  };

  const checkout = async () => {
    const payload = {
      items: cart.map((l) => ({ item_id: l.item_id, name: l.name, qty: l.qty, price: l.price, discount: l.discount })),
      total_discount: totalDiscount,
      payment_method: payment,
      paid_amount: paid,
      customer,
      print_receipt: printReceipt,
    };
    const saveOffline = () => {
      const sale = buildOfflineSale(payload, cart);
      enqueueSale(sale); refreshQueue();
      const upd = items.map((i) => { const l = cart.find((c) => c.item_id === i.id); return l ? { ...i, stock: i.stock - l.qty } : i; });
      setItems(upd); cacheItems(upd);
      finishLocal(sale);
      toast.warning(`Offline: transaksi ${sale.number} disimpan di antrian`);
      doPrint(sale);
    };
    if (!online || !navigator.onLine) {
      if (isCredit) { toast.error("Transaksi hutang membutuhkan koneksi online"); return; }
      saveOffline(); return;
    }
    try {
      const { data } = await api.post("/sales", payload);
      finishLocal(data);
      toast.success(isCredit && data.credit_remaining > 0 ? `Transaksi ${data.number} — hutang ${rupiah(data.credit_remaining)} dicatat` : `Transaksi ${data.number} berhasil`);
      loadItems();
      doPrint(data);
    } catch (e) {
      if (!e.response) { saveOffline(); return; }
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const discardQueued = (cid) => { setQueue(getQueue().filter((s) => s.client_id !== cid)); refreshQueue(); };

  const exit = async () => { await logout(); navigate("/login"); };

  return (
    <div className="h-screen flex flex-col bg-slate-950 text-white overflow-hidden">
      <header className="h-16 shrink-0 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-5">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-orange-500 flex items-center justify-center">
            <Store className="h-5 w-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-bold font-display">Kasir · Sumber Harapan Bitung</div>
            <div className="text-xs text-slate-400">Shift: {user.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {held && (
            <span className="text-xs bg-amber-500/20 text-amber-400 px-3 py-1.5 rounded-full border border-amber-500/30">
              1 transaksi ditahan
            </span>
          )}
          {queue.length > 0 && (
            <button onClick={() => setQueueOpen(true)} data-testid="pos-offline-queue-badge"
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border text-sky-300 border-sky-500/30 bg-sky-500/10">
              <CloudUpload className={`h-3.5 w-3.5 ${syncing ? "animate-bounce" : ""}`} /> {queue.length} menunggu sinkron
            </button>
          )}
          <button onClick={() => setPrinterOpen(true)} data-testid="pos-printer-settings-button"
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${printerConnected() ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-slate-300 border-slate-700 bg-slate-800"}`}>
            <Printer className="h-3.5 w-3.5" /> {printerConnected() ? "Printer Siap" : "Printer"}
          </button>
          <button onClick={() => setOnline(!online)} data-testid="pos-online-toggle"
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${online ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-amber-400 border-amber-500/30 bg-amber-500/10"}`}>
            {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
            {online ? "Online" : "Offline"}
          </button>
          <Button onClick={exit} variant="ghost" data-testid="pos-exit-button"
            className="text-slate-300 hover:text-white hover:bg-slate-800">
            <LogOut className="h-4 w-4 mr-2" /> Keluar
          </Button>
        </div>
      </header>

      {isStaff && (
        <nav className="h-10 shrink-0 bg-slate-900/70 border-b border-slate-800 flex items-center gap-1 px-4 overflow-x-auto" data-testid="pos-staff-navbar">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-2">Menu cepat</span>
          {QUICK_NAV.map((m) => (
            <NavLink key={m.to} to={m.to} data-testid={`pos-nav-${m.to.slice(1)}`}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md text-slate-300 hover:bg-orange-500 hover:text-white transition-colors whitespace-nowrap">
              <m.icon className="h-3.5 w-3.5" /> {m.label}
            </NavLink>
          ))}
        </nav>
      )}

      <div className="flex-1 grid grid-cols-12 gap-4 p-4 overflow-hidden">
        <div className="col-span-12 lg:col-span-7 xl:col-span-8 flex flex-col overflow-hidden">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={barcodeEnter}
              placeholder="Scan barcode / cari nama barang..." data-testid="pos-barcode-search-input"
              className="pl-10 h-12 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 text-base" />
          </div>
          <div className="flex-1 overflow-y-auto grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 content-start pr-1">
            {filtered.map((i) => (
              <button key={i.id} onClick={() => addToCart(i)} data-testid={`pos-item-${i.barcode}`}
                className="text-left rounded-xl bg-slate-900 border border-slate-800 p-3 hover:border-orange-500 hover:bg-slate-800 transition-all disabled:opacity-40"
                disabled={i.stock <= 0}>
                <div className="text-[10px] font-mono text-slate-500">{i.barcode}</div>
                <div className="text-sm font-medium leading-tight mt-1 line-clamp-2 h-9">{i.name} <span className="text-slate-400 font-normal">({i.unit})</span></div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-orange-400 font-bold mono text-sm">{rupiah(i.price_sell)}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${i.stock <= i.min_stock ? "bg-rose-500/20 text-rose-400" : "bg-slate-800 text-slate-400"}`}>
                    {i.stock} {i.unit}
                  </span>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-center text-slate-500 py-16">Barang tidak ditemukan</div>
            )}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 xl:col-span-4 flex flex-col bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold">
              <ShoppingCart className="h-4 w-4 text-orange-400" /> Keranjang
            </div>
            <div className="flex gap-1">
              <button onClick={hold} data-testid="pos-hold-transaction-button"
                className="text-xs flex items-center gap-1 text-amber-400 hover:bg-slate-800 px-2 py-1 rounded">
                <PauseCircle className="h-3.5 w-3.5" /> Tahan
              </button>
              {held && (
                <button onClick={resume} data-testid="pos-resume-button"
                  className="text-xs flex items-center gap-1 text-emerald-400 hover:bg-slate-800 px-2 py-1 rounded">
                  <PlayCircle className="h-3.5 w-3.5" /> Lanjut
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {cart.length === 0 && <div className="text-center text-slate-500 py-16 text-sm">Keranjang kosong</div>}
            {cart.map((l) => (
              <div key={l.item_id} className="rounded-lg bg-slate-800/60 border border-slate-700 p-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-medium leading-tight">{l.name}</div>
                  <button onClick={() => removeLine(l.item_id)}><Trash2 className="h-4 w-4 text-rose-400" /></button>
                </div>
                <button onClick={() => openPriceEdit(l)} data-testid={`pos-edit-price-${l.item_id}`}
                  className="text-xs text-slate-400 mono mt-0.5 inline-flex items-center gap-1 hover:text-orange-400 transition-colors">
                  {rupiah(l.price)} <Pencil className="h-3 w-3" />
                </button>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty(l.item_id, l.qty - 1)} className="h-7 w-7 rounded bg-slate-700 flex items-center justify-center"><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-8 text-center mono">{l.qty}</span>
                    <button onClick={() => setQty(l.item_id, l.qty + 1)} className="h-7 w-7 rounded bg-slate-700 flex items-center justify-center"><Plus className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setDiscOpen(l.item_id)} className="ml-1 text-[11px] text-orange-400 flex items-center gap-0.5">
                      <Percent className="h-3 w-3" /> {l.discount > 0 ? rupiah(l.discount) : "Diskon"}
                    </button>
                  </div>
                  <span className="mono font-semibold text-sm">{rupiah(l.qty * l.price - l.discount)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-slate-800 space-y-3">
            {(!online || !navigator.onLine) && (
              <div className="text-xs bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="pos-offline-notice">
                <WifiOff className="h-3.5 w-3.5" /> Mode offline — transaksi disimpan lokal & disinkron otomatis saat online.
              </div>
            )}

            <div className="grid grid-cols-4 gap-2">
              {PAY.map((p) => (
                <button key={p.key} onClick={() => setPayment(p.key)} data-testid={`pos-pay-${p.key}`}
                  className={`py-2 rounded-lg border text-xs font-medium transition-colors ${payment === p.key ? "bg-orange-500 text-white border-orange-500" : "border-slate-700 text-slate-300 hover:border-orange-500"}`}>
                  {p.label}
                </button>
              ))}
            </div>

            <Input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder={isCredit ? "Nama pelanggan (wajib untuk hutang)" : "Nama pelanggan (opsional)"}
              data-testid="pos-customer-input" className={`h-9 bg-slate-800 text-white placeholder:text-slate-500 text-sm ${isCredit && !customer.trim() ? "border-amber-500" : "border-slate-700"}`} />

            <div className="flex justify-between text-sm text-slate-400">
              <span>Subtotal</span><span className="mono">{rupiah(subtotal)}</span>
            </div>
            <div className="flex justify-between items-center text-sm text-slate-400">
              <span>Diskon Total</span>
              <div className="w-32"><RupiahInput value={totalDiscount} onChange={setTotalDiscount} className="h-8 bg-slate-800 border-slate-700 text-white text-right" /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-400">Uang Dibayar</span>
                <button onClick={() => setPaid(grandTotal)} data-testid="pos-exact-cash-button"
                  className="text-[11px] font-medium text-orange-400 hover:text-orange-300">Uang Pas</button>
              </div>
              <RupiahInput value={paid} onChange={setPaid} data-testid="pos-paid-input"
                className="h-11 bg-slate-800 border-slate-700 text-white text-right text-lg font-bold" />
            </div>

            <div className="rounded-xl bg-slate-800/80 border border-slate-700 overflow-hidden" data-testid="pos-financial-summary">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-700">
                <span className="text-sm font-bold text-slate-300">TOTAL</span>
                <span className="mono text-2xl font-bold text-orange-400" data-testid="pos-total-value">{rupiah(grandTotal)}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-700">
                <span className="text-sm font-bold text-slate-300">DIBAYAR</span>
                <span className="mono text-2xl font-bold text-white" data-testid="pos-paid-value">{rupiah(paid || 0)}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-sm font-bold text-slate-300">{isCredit && kurang > 0 ? "SISA HUTANG" : kurang > 0 ? "KURANG" : "KEMBALIAN"}</span>
                <span className={`mono text-2xl font-bold ${kurang > 0 ? (isCredit ? "text-amber-400" : "text-rose-400") : "text-emerald-400"}`} data-testid="pos-change-value">
                  {rupiah(kurang > 0 ? kurang : change)}
                </span>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={printReceipt} onChange={(e) => setPrintReceipt(e.target.checked)}
                data-testid="pos-print-toggle" className="h-4 w-4 accent-orange-500" />
              <Printer className="h-4 w-4" /> Cetak struk {printerConnected() ? "(thermal langsung)" : "(print browser)"}
            </label>

            <Button onClick={checkout} disabled={!canPay} data-testid="pos-checkout-button"
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:text-slate-400 text-base font-bold">
              {cart.length === 0 ? "Keranjang Kosong" : isCredit ? (customer.trim() ? "Simpan Hutang & Cetak" : "Isi Nama Pelanggan") : kurang > 0 ? `Kurang ${rupiah(kurang)}` : "Selesai & Cetak"}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={!!priceEdit} onOpenChange={(o) => { if (!o) { setPriceEdit(null); setApproverUser(""); setApproverPass(""); } }}>
        <DialogContent className="sm:max-w-xs" data-testid="pos-price-edit-dialog">
          <DialogHeader><DialogTitle>Ubah Harga — {priceEdit?.name}</DialogTitle></DialogHeader>
          {priceEdit && (
            <div className="space-y-3">
              <div className="space-y-1">
                <span className="text-xs text-slate-500">Harga Baru</span>
                <RupiahInput value={priceEdit.price} onChange={(v) => setPriceEdit((p) => ({ ...p, price: v }))} data-testid="pos-price-new-input" />
              </div>
              {user.role === "kasir" && (
                <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
                  <p className="text-xs text-amber-800 font-medium">Perlu otorisasi Master / Admin / GM</p>
                  <Input value={approverUser} onChange={(e) => setApproverUser(e.target.value)} placeholder="Username pemberi izin" data-testid="pos-price-approver-username" className="h-9" />
                  <Input type="password" value={approverPass} onChange={(e) => setApproverPass(e.target.value)} placeholder="Password pemberi izin" data-testid="pos-price-approver-password" className="h-9" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={savePrice} disabled={savingPrice} className="bg-orange-500 hover:bg-orange-600" data-testid="pos-price-save-button">
              {savingPrice ? "Memproses..." : "Simpan Harga"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!discOpen} onOpenChange={(o) => !o && setDiscOpen(null)}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader><DialogTitle>Diskon Item</DialogTitle></DialogHeader>
          <RupiahInput value={cart.find((l) => l.item_id === discOpen)?.discount || 0}
            onChange={(v) => setCart((c) => c.map((l) => l.item_id === discOpen ? { ...l, discount: v } : l))} />
          <DialogFooter><Button onClick={() => setDiscOpen(null)} className="bg-orange-500 hover:bg-orange-600">Selesai</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={queueOpen} onOpenChange={setQueueOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Antrian Transaksi Offline</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="pos-offline-queue-list">
            {queue.map((s) => (
              <div key={s.client_id} className="border rounded-lg p-3 text-sm flex items-center justify-between gap-3">
                <div>
                  <div className="font-mono font-semibold">{s.number} · {rupiah(s.total)}</div>
                  <div className="text-xs text-slate-500">{new Date(s.offline_at).toLocaleString("id-ID")} · {s.items.length} item · {s.payment_method.toUpperCase()}</div>
                  {s.error && <div className="text-xs text-rose-600 mt-1">Gagal: {s.error}</div>}
                </div>
                {s.error && <Button size="sm" variant="outline" className="text-rose-600" onClick={() => discardQueued(s.client_id)}>Hapus</Button>}
              </div>
            ))}
            {queue.length === 0 && <div className="text-center text-slate-400 py-6 text-sm">Tidak ada antrian</div>}
          </div>
          <DialogFooter>
            <Button onClick={() => syncQueue(false)} disabled={syncing || !online} data-testid="pos-sync-now-button" className="bg-sky-600 hover:bg-sky-700">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} /> Sinkron Sekarang
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PrinterDialog open={printerOpen} onOpenChange={setPrinterOpen} lastSale={lastSale} cashier={user.name} />

      {lastSale && <Receipt sale={lastSale} cashier={user.name} />}
    </div>
  );
}

function Receipt({ sale, cashier }) {
  return (
    <div id="receipt-print" style={{ display: "none" }}>
      <div style={{ textAlign: "center", fontWeight: 700 }}>TOKO BANGUNAN</div>
      <div style={{ textAlign: "center", fontWeight: 700 }}>SUMBER HARAPAN</div>
      <div style={{ textAlign: "center", fontSize: 10 }}>Bitung, Sulawesi Utara</div>
      <div style={{ fontSize: 11, marginTop: 4 }}>No: {sale.number}</div>
      <div style={{ fontSize: 11 }}>Kasir: {cashier}</div>
      <div style={{ fontSize: 11 }}>{new Date(sale.created_at).toLocaleString("id-ID")}</div>
      <div style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />
      {sale.items.map((l, i) => (
        <div key={i} style={{ fontSize: 11, marginBottom: 2 }}>
          <div>{l.name}</div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>{l.qty} x {formatNumber(l.price)}{l.discount ? ` -${formatNumber(l.discount)}` : ""}</span>
            <span>{formatNumber(l.subtotal)}</span>
          </div>
        </div>
      ))}
      <div style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />
      <Row label="Subtotal" val={sale.subtotal} />
      {sale.total_discount > 0 && <Row label="Diskon" val={sale.total_discount} />}
      <Row label="TOTAL" val={sale.total} bold />
      <Row label="Bayar" val={sale.paid_amount} />
      <Row label="Kembali" val={sale.change} />
      <div style={{ fontSize: 11 }}>Metode: {(sale.payment_method || "").toUpperCase()}</div>
      <div style={{ textAlign: "center", fontSize: 11, marginTop: 6 }}>Terima kasih atas kunjungan Anda</div>
      <div style={{ textAlign: "center", fontSize: 10 }}>Barang yang sudah dibeli dapat ditukar sesuai ketentuan</div>
    </div>
  );
}
function Row({ label, val, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: bold ? 700 : 400 }}>
      <span>{label}</span><span>Rp {formatNumber(val)}</span>
    </div>
  );
}
